import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { useScrollDirection } from 'use-scroll-direction'
import OnTheLineModules from '@toasttab/otl/src/components/PageModules/OnTheLineModules/OnTheLineModules'
import Disclaimer from '@toasttab/otl/src/components/PageModules/Disclaimer'
import { OTLContext } from '@toasttab/shared/src/context/OTLContext'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import DataArticleTableOfContents from './DataArticleTableOfContents'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { ContentfulMdsAccordion } from '@toasttab/shared/src/components/organisms/Contentful/Content'

const DataArticle = ({ entry, formCategories }: any) => {
  const [activeSection, setActiveSection] = useState(null)
  const [tocData, setTocData] = useState({ links: [], ids: [] })

  const { region } = useI18next()
  const { scrollDirection } = useScrollDirection()

  const otlParams = useContext(OTLContext)
  const onTheLineModules = entry.content
  const formCategory = formCategories.filter(
    (n: { internalName: string }) => n.internalName === `[${region}] Email Only`
  )[0]
  const articleRef = useRef<any | null>(null)
  const contentRef = useRef(null)

  useEffect(() => {
    const getAllContentLinks: any = articleRef.current.getElementsByTagName(`a`)
    const contentLinks = Array.from(getAllContentLinks)
    contentLinks.forEach((cL: any) => {
      cL.setAttribute('target', '_blank')
    })
  }, [])

  const handleSections = useCallback(
    (entries: any) => {
      entries.forEach((entry: any) => {
        if (
          entry.isIntersecting &&
          entry?.target?.id &&
          tocData?.ids.includes(entry.target.id)
        ) {
          const currentIndex = tocData.ids.indexOf(entry.target.id)
          const activeIndex = tocData.ids.indexOf(activeSection)
          if (
            (activeIndex < 0 ||
              (scrollDirection === 'DOWN' && currentIndex > activeIndex) ||
              (scrollDirection === 'UP' && currentIndex < activeIndex)) &&
            activeSection !== entry.target.id
          ) {
            setActiveSection(entry.target.id)
          }
        }
      })
    },
    [activeSection, scrollDirection, tocData]
  )

  useEffect(() => {
    const links = onTheLineModules
      .map((module: any) => {
        if (module.addToTableOfContents) {
          // Get values from General Body
          if (module.tableOfContentsId && module.tableOfContentsTitle) {
            return {
              anchor: module.tableOfContentsId,
              heading: module.tableOfContentsTitle
            }
          }
          // Get values from other content types
          if (module.heading && module.uniqueId) {
            return {
              anchor: module.uniqueId,
              heading: module.heading
            }
          }
        }
        return null
      })
      .filter((link: any) => !!link?.anchor)

    if (entry.customDisclaimer?.raw || entry.showDisclaimer) {
      links.push({
        anchor: 'disclaimer',
        heading: 'Disclaimer'
      })
    }

    setTocData({
      links,
      ids: links.map((link: any) => link.anchor)
    })
  }, [entry, onTheLineModules])

  useEffect(() => {
    const sectionsArr = Array.prototype.slice.call(
      contentRef?.current?.children
    )
    sectionsArr?.forEach((section: any) => {
      let observer = new IntersectionObserver(handleSections, {
        rootMargin: `-20% 0px -20% 0px`,
        threshold: [0, 1]
      })
      observer.observe(section)
    })
  }, [contentRef, handleSections, tocData])

  return (
    <section
      className='ds-container ds-grid pb-10 px-4 xl:px-0 relative'
      data-testid='DataArticle'
      ref={articleRef}
    >
      {tocData?.links.length > 0 && (
        <nav className='mb-2 col-span-4 md:col-span-6 md:mt-6 lg:mt-0 lg:col-span-3 lg:mb-0 lg:pr-12'>
          <div className='border-b border-t border-color-light py-6 lg:border-0 lg:py-0 lg:sticky lg:top-32'>
            <DataArticleTableOfContents
              activeSection={activeSection}
              links={tocData.links}
            />
          </div>
        </nav>
      )}
      <div
        className='data-article-content [&>div>h2]:text-[30px] col-span-4 md:col-span-6 lg:col-span-9 [&>div:first-child]:mt-0 [&>div:first-child>p]:mt-0 lg:pl-12 lg:border-l lg:border-color-light'
        ref={contentRef}
      >
        <OnTheLineModules
          entryId={entry.remoteId}
          formCategory={formCategory}
          isPreview={otlParams.isPreview}
          onTheLineModules={onTheLineModules}
        />
        {(entry.customDisclaimer?.raw || entry.showDisclaimer) && (
          <div id='disclaimer' className='font-sansPro'>
            {entry.customDisclaimer?.raw &&
              renderRichText(entry.customDisclaimer)}
            {!entry.customDisclaimer?.raw && entry.showDisclaimer && (
              <Disclaimer />
            )}
          </div>
        )}
      </div>
      {entry.accordion && (
        <div className='col-span-4 md:col-span-6 lg:col-span-12 border-t border-color-light mt-8'>
          <ContentfulMdsAccordion
            {...entry.accordion}
            headingClasses='text-[31px] font-effra !font-semibold !px-0 !pt-2 md:!pt-12'
          />
        </div>
      )}
    </section>
  )
}

export default DataArticle
